import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { faColumns3, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex } from 'antd'
import Title from 'antd/es/typography/Title'
import { computed, runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import {
  CandidateCustomColumnFragment,
  ViewColumnTemplateFragment,
  ViewColumnType,
} from 'types/graphql'

import { Drawer } from 'src/components/Drawer'
import CandidateSectionHeader, {
  CANDIDATE_SECTION_KEY_PREFIX,
} from 'src/components/TableViews/ManageColumnsDrawer/CandidateSectionHeader'
import HiddenColumnsHeader, {
  HIDDEN_SECTION_HEADER_KEY,
} from 'src/components/TableViews/ManageColumnsDrawer/HiddenColumnsHeader'
import ManageColumnsDrawerItem from 'src/components/TableViews/ManageColumnsDrawer/ManageColumnsDrawerItem'
import { StyledHeader } from 'src/components/TableViews/ManageColumnsDrawer/styled-components'
import UpdateCustomColumnModal from 'src/components/TableViews/ManageColumnsDrawer/UpdateCustomColumnModal'
import { useUpdateViewMutation } from 'src/fetch/views'
import {
  useCreateViewCustomColumnTemplateMutation,
  useGetCandidateCustomColumnsQuery,
} from 'src/fetch/views/viewColumnTemplates'
import useDisclosure, { UseDisclosureReturn } from 'src/hooks/use-disclosure'
import { TableView } from 'src/models/TableViews/TableView'
import { TableViewColumn } from 'src/models/TableViews/TableViewColumn'
import { TableViewManager } from 'src/models/TableViews/TableViewManager'
import { groupBy } from 'src/utils/collections'
import { QueryKey } from 'src/utils/queryClient'

const generateCandidateColumnId = (candidate: CandidateCustomColumnFragment): string =>
  `candidate:${candidate.columnType}:${candidate.sourceId}`

const mapCandidateToColumnTemplate = (
  candidate: CandidateCustomColumnFragment
): ViewColumnTemplateFragment => ({
  __typename: 'ViewColumnTemplate',
  columnType: candidate.columnType,
  dataType: 'String',
  generatedId: generateCandidateColumnId(candidate),
  labelSentenceCase: candidate.label,
  labelTitleCase: candidate.label,
  fieldTemplates: [],
  filterOptions: [],
  includeByDefault: false,
  isFilterable: false,
  isSortable: false,
  isNullable: false,
  usedInViews: [],
})

type Props = {
  modalState: UseDisclosureReturn
  tableViewManager: TableViewManager
  view: TableView
}
const ManageColumnsDrawer = ({ modalState, tableViewManager, view }: Props) => {
  const columnManager = view.columnManager
  const queryClient = useQueryClient()
  const { mutateAsync: updateView } = useUpdateViewMutation()
  const { mutateAsync: createCustomColumnTemplate } = useCreateViewCustomColumnTemplateMutation()
  const createCustomColumnModalState = useDisclosure()

  const { data: candidateColumnsData } = useGetCandidateCustomColumnsQuery({
    viewType: tableViewManager.viewType,
    viewDashboardId: tableViewManager.viewDashboardId,
    recordTypeId: tableViewManager.recordTypeId,
  })
  const candidateColumns = candidateColumnsData?.getCandidateCustomColumns?.candidates

  // Tracks candidates that have been created as real columns this session.
  // Lets us hide the candidate drawer item immediately (before the query refetches).
  // Reset when candidateColumns changes (i.e. query refetched with fresh server data).
  const [consumedCandidateIds, setConsumedCandidateIds] = useState(new Set<string>())
  useEffect(() => {
    setConsumedCandidateIds(new Set())
  }, [candidateColumns])

  const [loadingColumnIds, setLoadingColumnIds] = useState(new Set<string>())
  const inFlightCreationsRef = useRef(0)
  const saveInFlightRef = useRef(false)
  const needsResaveRef = useRef(false)

  const candidateTemplatesBySection = useMemo(() => {
    const available = (candidateColumns ?? []).filter(
      (c) => !consumedCandidateIds.has(generateCandidateColumnId(c))
    )
    const grouped = groupBy(available, (c) => c.sectionLabel)
    const map = new Map<string, ViewColumnTemplateFragment[]>()
    for (const [sectionLabel, candidates] of Object.entries(grouped)) {
      map.set(sectionLabel, candidates?.map(mapCandidateToColumnTemplate) ?? [])
    }
    return map
  }, [candidateColumns, consumedCandidateIds])

  const candidateLookup = useMemo(() => {
    const map = new Map<string, CandidateCustomColumnFragment>()
    for (const candidate of candidateColumns ?? []) {
      const id = generateCandidateColumnId(candidate)
      if (!consumedCandidateIds.has(id)) {
        map.set(id, candidate)
      }
    }
    return map
  }, [candidateColumns, consumedCandidateIds])

  const sectionLabelByColumnType = useMemo(() => {
    const map = new Map<ViewColumnType, string>()
    for (const info of candidateColumnsData?.getCandidateCustomColumns
      ?.candidateDiscoverableColumnTypeInfo ?? []) {
      map.set(info.columnType, info.sectionLabel)
    }
    return map
  }, [candidateColumnsData?.getCandidateCustomColumns?.candidateDiscoverableColumnTypeInfo])

  const candidateColumnTypes = useMemo(
    () => new Set(sectionLabelByColumnType.keys()),
    [sectionLabelByColumnType]
  )

  const isEditableCustomColumn = useCallback(
    (columnType: ViewColumnType) =>
      view.customizableColumnTemplatesByType.has(columnType) &&
      !candidateColumnTypes.has(columnType),
    [view.customizableColumnTemplatesByType, candidateColumnTypes]
  )

  const flushSave = useCallback(async () => {
    if (saveInFlightRef.current) {
      needsResaveRef.current = true
      return
    }
    saveInFlightRef.current = true
    try {
      await runInAction(async () => {
        await updateView({
          id: view.id,
          input: tableViewManager.getCreateViewInput(view.databaseProtocol),
        })
      })
      await queryClient.invalidateQueries({ queryKey: [QueryKey.CandidateCustomColumns] })
    } finally {
      saveInFlightRef.current = false
      if (needsResaveRef.current) {
        needsResaveRef.current = false
        await flushSave()
      }
    }
  }, [queryClient, tableViewManager, updateView, view.databaseProtocol, view.id])

  const createAndRegisterCandidate = useCallback(
    async (
      columnGeneratedId: string,
      afterAddTemplate?: (generatedId: string) => void
    ): Promise<string> => {
      const candidate = candidateLookup.get(columnGeneratedId)
      if (!candidate) {
        return ''
      }
      setLoadingColumnIds((prev) => new Set([...prev, columnGeneratedId]))
      inFlightCreationsRef.current++
      try {
        const result = await createCustomColumnTemplate({
          input: {
            columnType: candidate.columnType,
            configuredLabel: candidate.label,
            fieldTemplateIds: [candidate.sourceId],
            recordTypeId: tableViewManager.recordTypeId,
            viewDashboardId: tableViewManager.viewDashboardId,
          },
        })
        const newGeneratedId = result.createViewCustomColumnTemplate.generatedId
        runInAction(() => {
          tableViewManager.columnTemplateStore.addColumnTemplate(
            result.createViewCustomColumnTemplate
          )
          afterAddTemplate?.(newGeneratedId)
        })
        setConsumedCandidateIds((prev) => new Set([...prev, columnGeneratedId]))
        return newGeneratedId
      } catch {
        return ''
      } finally {
        setLoadingColumnIds((prev) => {
          const next = new Set(prev)
          next.delete(columnGeneratedId)
          return next
        })
        inFlightCreationsRef.current--
        if (inFlightCreationsRef.current === 0) {
          await flushSave()
        }
      }
    },
    [
      candidateLookup,
      flushSave,
      tableViewManager.recordTypeId,
      tableViewManager.viewDashboardId,
      tableViewManager.columnTemplateStore,
      createCustomColumnTemplate,
    ]
  )

  // Translates a dragged item's ID to a real column generatedId,
  // creating the column first if the ID belongs to a candidate.
  const resolveColumnId = useCallback(
    async (draggedColumnId: string): Promise<string | undefined> => {
      const existingTemplate = tableViewManager.columnTemplates.find(
        (ct) => ct.generatedId === draggedColumnId
      )
      if (existingTemplate) {
        return draggedColumnId
      }
      if (candidateLookup.has(draggedColumnId)) {
        return await createAndRegisterCandidate(draggedColumnId)
      }
      return undefined
    },
    [tableViewManager.columnTemplates, candidateLookup, createAndRegisterCandidate]
  )

  const handleColumnVisibilityToggled = useCallback(
    async (columnGeneratedId: string) => {
      if (candidateLookup.has(columnGeneratedId)) {
        await createAndRegisterCandidate(columnGeneratedId, (genId) => {
          columnManager.toggleColumnTypeVisibility(genId)
        })
        return
      }
      const existingTemplate = tableViewManager.columnTemplates.find(
        (ct) => ct.generatedId === columnGeneratedId
      )
      if (!existingTemplate) {
        return
      }
      runInAction(() => {
        columnManager.toggleColumnTypeVisibility(columnGeneratedId)
      })
      await flushSave()
    },
    [
      candidateLookup,
      columnManager,
      createAndRegisterCandidate,
      flushSave,
      tableViewManager.columnTemplates,
    ]
  )

  const handleColumnsReordered = useCallback(
    async (e: DragEndEvent) => {
      const draggedColumnId = e.active?.id as string
      if (!e.over) {
        return
      }
      const displacedColumnId = e.over?.id as string
      await runInAction(async () => {
        const sourceIndex = columnManager.columns.findIndex(
          (c) => c.columnTemplateGeneratedId === draggedColumnId
        )
        const destinationIndex = columnManager.columns.findIndex(
          (c) => c.columnTemplateGeneratedId === displacedColumnId
        )
        const newColumns = [...columnManager.columns]

        if (sourceIndex < 0) {
          const isDestinationShown =
            displacedColumnId === HIDDEN_SECTION_HEADER_KEY || destinationIndex >= 0
          if (!isDestinationShown) {
            return
          }
          const realColumnId = await resolveColumnId(draggedColumnId)
          if (!realColumnId) {
            return
          }
          const newColumn = new TableViewColumn({
            columnGeneratedId: realColumnId,
            columnTemplateStore: tableViewManager.columnTemplateStore,
            displayIndex: destinationIndex,
          })
          if (displacedColumnId === HIDDEN_SECTION_HEADER_KEY) {
            newColumns.push(newColumn)
          } else if (destinationIndex >= 0) {
            newColumns.splice(destinationIndex, 0, newColumn)
          }
        } else if (destinationIndex < 0) {
          if (columnManager.columns.length <= 1) {
            return
          }
          newColumns.splice(sourceIndex, 1)
        } else {
          const sourceColumn = newColumns.splice(sourceIndex, 1)[0]
          if (sourceColumn) {
            newColumns.splice(destinationIndex, 0, sourceColumn)
          }
        }

        columnManager.columns = newColumns
      })
      await flushSave()
    },
    [columnManager, resolveColumnId, flushSave, tableViewManager.columnTemplateStore]
  )

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
        distance: 1,
      },
    })
  )
  const verticalModifiers = useMemo(() => [restrictToVerticalAxis], [])

  const hiddenColumnTemplates = computed(() =>
    tableViewManager.columnTemplates
      .filter(
        (ct) =>
          !columnManager.columnsByColumnGeneratedId.has(ct.generatedId) &&
          !sectionLabelByColumnType.has(ct.columnType)
      )
      .sort((a, b) => a.labelSentenceCase.localeCompare(b.labelSentenceCase))
  ).get()

  const allCandidateTemplatesBySection = computed(() => {
    const map = new Map<string, ViewColumnTemplateFragment[]>()

    for (const [sectionLabel, templates] of candidateTemplatesBySection) {
      map.set(sectionLabel, [...templates])
    }

    for (const ct of tableViewManager.columnTemplates) {
      if (columnManager.columnsByColumnGeneratedId.has(ct.generatedId)) {
        continue
      }
      const sectionLabel = sectionLabelByColumnType.get(ct.columnType)
      if (!sectionLabel) {
        continue
      }
      const existing = map.get(sectionLabel) ?? []
      existing.push(ct)
      map.set(sectionLabel, existing)
    }

    for (const templates of map.values()) {
      templates.sort((a, b) => a.labelSentenceCase.localeCompare(b.labelSentenceCase))
    }

    return map
  }).get()

  const keys = computed(() => [
    ...columnManager.columns.map((c) => c.columnTemplateGeneratedId),
    HIDDEN_SECTION_HEADER_KEY,
    ...hiddenColumnTemplates.map((ct) => ct.generatedId),
    ...[...allCandidateTemplatesBySection.entries()].flatMap(([sectionLabel, templates]) => [
      `${CANDIDATE_SECTION_KEY_PREFIX}${sectionLabel}`,
      ...templates.map((ct) => ct.generatedId),
    ]),
  ]).get()

  return (
    <Drawer {...modalState} title="Manage Columns" icon={faColumns3} width="400px">
      <Flex vertical gap="12px">
        <Flex justify="space-between" align="center">
          <Title level={4} style={{ margin: 0 }}>
            Columns
          </Title>
          {view.hasCustomizableColumnTemplates && (
            <Button
              size="small"
              onClick={createCustomColumnModalState.open}
              icon={<FontAwesomeIcon icon={faPlus} />}
            >
              Create column
            </Button>
          )}
        </Flex>
        {createCustomColumnModalState.isOpen && (
          <UpdateCustomColumnModal
            modalState={createCustomColumnModalState}
            mode="create"
            onColumnVisibilityToggled={handleColumnVisibilityToggled}
            tableViewManager={tableViewManager}
            view={view}
          />
        )}
        <Flex vertical>
          <DndContext
            sensors={sensors}
            modifiers={verticalModifiers}
            onDragEnd={handleColumnsReordered}
          >
            <SortableContext items={keys} strategy={verticalListSortingStrategy}>
              <StyledHeader>Shown in table</StyledHeader>
              {columnManager.columns.map((column) => {
                if (!column.columnTemplate) {
                  return null
                }
                return (
                  <ManageColumnsDrawerItem
                    columnTemplate={column.columnTemplate}
                    isEditable={isEditableCustomColumn(column.columnTemplate.columnType)}
                    isLoading={false}
                    isVisible
                    onColumnVisibilityToggled={handleColumnVisibilityToggled}
                    tableViewManager={tableViewManager}
                    view={view}
                    key={column.columnTemplateGeneratedId}
                  />
                )
              })}
              <HiddenColumnsHeader />
              {hiddenColumnTemplates.map((ct) => (
                <ManageColumnsDrawerItem
                  columnTemplate={ct}
                  isEditable={isEditableCustomColumn(ct.columnType)}
                  isLoading={false}
                  isVisible={false}
                  onColumnVisibilityToggled={handleColumnVisibilityToggled}
                  tableViewManager={tableViewManager}
                  view={view}
                  key={ct.generatedId}
                />
              ))}
              {[...allCandidateTemplatesBySection.entries()].map(([sectionLabel, templates]) => (
                <Fragment key={sectionLabel}>
                  <CandidateSectionHeader
                    id={`${CANDIDATE_SECTION_KEY_PREFIX}${sectionLabel}`}
                    label={sectionLabel}
                  />
                  {templates.map((ct) => (
                    <ManageColumnsDrawerItem
                      columnTemplate={ct}
                      isEditable={false}
                      isLoading={loadingColumnIds.has(ct.generatedId)}
                      isVisible={false}
                      onColumnVisibilityToggled={handleColumnVisibilityToggled}
                      tableViewManager={tableViewManager}
                      view={view}
                      key={ct.generatedId}
                    />
                  ))}
                </Fragment>
              ))}
            </SortableContext>
          </DndContext>
        </Flex>
      </Flex>
    </Drawer>
  )
}

export default observer(ManageColumnsDrawer)
